import { useMemo } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { LiquidationPill } from '@/shared/components/pills';
import { CompactSwapRoute } from '@/shared/components/swaps/SwapRouteV2';
import { SwapStatus } from '@/shared/components/swaps/SwapStatusV2';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getSpecificSwapRequestsQuery } from '@/shared/graphql/reporting/swaps';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useLiquidations } from '@/shared/hooks/useLiquidations';
import ChunksIcon from '@/shared/icons/flip-ui-kit/small/Chunks';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';

export const LiquidationsTable = ({
  idSs58,
  loanId,
  showAccountId = true,
}: {
  idSs58?: string;
  loanId?: string;
  showAccountId?: boolean;
}) => {
  const { isLoadingLiquidations, liquidationsData, paginationControls } = useLiquidations({
    idSs58,
    loanId,
  });

  const swapRequestNativeIds =
    liquidationsData?.allLiquidationSwapRequests?.nodes.map(
      (liquidation) => liquidation.swapRequestId,
    ) ?? [];

  const { data: swapRequestsInfo, isLoading: isLoadingSwapRequestsInfo } = useGqlQuery(
    getSpecificSwapRequestsQuery,
    {
      context: { clientName: 'reportingService' },
      variables: { swapRequestIds: swapRequestNativeIds },
      enabled: swapRequestNativeIds.length > 0,
      refetchInterval: TABLE_POLL_INTERVAL,
    },
  );

  const rows =
    useMemo(
      () =>
        liquidationsData?.allLiquidationSwapRequests?.nodes.map((liquidation) => {
          const swapRequest = swapRequestsInfo?.allSwapRequests?.nodes.find(
            (swap) => swap.swapRequestNativeId === liquidation.swapRequestId,
          );

          return {
            liquidation,
            swapRequest,
          };
        }),
      [liquidationsData, swapRequestsInfo],
    ) ?? [];

  return (
    <TableV2
      isLoading={isLoadingLiquidations || isLoadingSwapRequestsInfo}
      rows={rows.map(({ liquidation, swapRequest }) => (
        <ClickableTableRow
          key={liquidation.swapRequestId}
          href={buildChainflipExplorerLink(liquidation.swapRequestId, 'swaps')}
          className={classNames(
            'text-12',
            swapRequest?.status === 'IN_PROGRESS' &&
              'bg-cf-gray-4 hover:bg-[#303030] [&>td]:!border-cf-gray-5',
          )}
        >
          <td>
            <Link
              href={buildChainflipExplorerLink(liquidation.swapRequestId, 'swaps')}
              noPropagate
              underline
            >
              #{liquidation.swapRequestId}
            </Link>
          </td>
          <td>
            <Link
              href={buildChainflipExplorerLink(liquidation.loan.id, 'loans')}
              noPropagate
              underline
            >
              #{liquidation.loan.id}
            </Link>
          </td>
          <td className="whitespace-nowrap">
            {swapRequest ? (
              <CompactSwapRoute
                routeInfo={{
                  destinationAsset: swapRequest.destAsset,
                  sourceAsset: swapRequest.sourceAsset,
                }}
                swapRequest={swapRequest}
                status={swapRequest.status}
                showIntermediate={false}
              />
            ) : null}
          </td>
          {showAccountId && isNotNullish(liquidation.loan.borrower) && (
            <td className="whitespace-nowrap">
              <div className="flex items-center gap-x-2">
                <Tooltip content={liquidation.loan.borrower.idSs58}>
                  <Link
                    href={buildChainflipExplorerLink(liquidation.loan.borrower.idSs58, 'lps')}
                    target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
                    underline
                    noPropagate
                  >
                    {abbreviate(liquidation.loan.borrower.idSs58, 4)}
                  </Link>
                </Tooltip>
                <CopyButton textToCopy={liquidation.loan.borrower.idSs58} />
              </div>
            </td>
          )}
          <td>
            <div className="flex flex-col gap-y-1 font-aeonikMedium">
              <SwapStatus status={swapRequest?.status} />
              {swapRequest?.totalChunks && swapRequest.totalChunks > 1 && (
                <div className="flex gap-x-1">
                  <ChunksIcon className="text-cf-light-3" />
                  {swapRequest.executedChunks}/{swapRequest.totalChunks}
                </div>
              )}
            </div>
          </td>
          <td>
            <div className="flex items-center gap-x-1 font-aeonikMedium">
              <LiquidationPill liquidationType={swapRequest?.liquidationType} />
            </div>
          </td>
          <td
            className="whitespace-nowrap text-right text-cf-light-2"
            title={
              swapRequest?.startedBlockTimestamp
                ? new Date(swapRequest.startedBlockTimestamp).toLocaleString()
                : 'A few seconds ago'
            }
          >
            <div>
              {swapRequest?.startedBlockTimestamp && (
                <AgeWithTooltip timestamp={swapRequest.startedBlockTimestamp} />
              )}
            </div>
          </td>
        </ClickableTableRow>
      ))}
      paginationControls={paginationControls}
      emptyRowsContent="No liquidations here yet"
      columns={[
        'Swap ID',
        'Loan ID',
        'Collateral Asset',
        ...(showAccountId ? ['Account ID'] : []),
        'Status',
        'Type',
        { name: 'Age', alignment: 'right' },
      ]}
    />
  );
};
